import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {default as EntityLinker, ENTITY_TYPES} from '@adsk/bim360-matrix-react-entity-linker';
import meetingsPlugin from '@adsk/bim360-matrix-react-entity-linker-plugins/es/plugins/meeting-item';
import rfisPlugin from '@adsk/bim360-matrix-react-entity-linker-plugins/es/plugins/rfi';
import assetsPlugin from '@adsk/bim360-matrix-react-entity-linker-plugins/es/plugins/asset';

import {issueAttributesType} from '../../constants/proptype-validation';
import i18n from '../../utils/i18n';
import {viewTypes} from '../../constants/view-types';
import featureFlags, {FEATURE_FLAGS} from '../../utils/featureFlags';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {linkEntityToIssue, openAddReferenceModal} from '../../utils/analytics-helpers';
import {
  getRfiFormattedAttachmentsAttributes,
  getRfiFormattedLinkedDocument,
  hasPermittedAttribute,
  getRfiFormattedSheetWithViewableId
} from '../../utils/issues-helpers';
import {linkIssueToEntityAction, trackEvent, unlinkIssueFromEntityAction} from '../../app/actions';
import {
  getActiveUsers,
  getAllOxygenMappings,
  getAttachmentsList,
  getCurrentProject,
  getCurrentUserInMcFormat,
  getCurrentViewType,
  getProjectCompanies,
  getProjectRoles
} from '../../app/selectors';
import {connect} from '../../app/connect';

const IssuePreviewDetailsTabLinkedEntitiesSection = ({
  issue,
  attachments,
  container,
  viewType,
  currentUser,
  currentProject,
  usersInProject,
  rolesInProject,
  companiesInProject,
  dispatch
}) => {
  const baseClassName = 'IssuePreviewDetailsTabLinkedEntitiesSection';
  const classes = cx(baseClassName, {
    [`${baseClassName}--narrow`]: viewType === viewTypes.DOCUMENT,
    [`${baseClassName}--wide`]: viewType === viewTypes.PROJECT
  });

  const allUsers = usersInProject || [];
  const updatedUsers = featureFlags.get(FEATURE_FLAGS.ADD_USER_ROLES_DETAIL)
    ? allUsers.map(user => ({
      ...user,
      roles:
            user.roles ?? (user.roleIds?.map(roleId => {
              const role = user.rolesMap[roleId];
              return {
                id: role.id,
                project_id: role.projectId,
                oxygen_id: role.oxygenId,
                ea_role_id: role.eaRoleId,
                name: role.name,
                status: role.status
              };
            }) || [])
    }))
    : allUsers;

  // allowEditingLinks is being set according to 'title' attribute because currently there is no specific attribute
  // that refers if a user can add links.
  const allowEditingLinks = hasPermittedAttribute(issue, ISSUE_ATTRIBUTE_NAMES.TITLE);

  const addRelationships = (linkedIssue, addedLinks) => {
    dispatch(trackEvent(linkEntityToIssue(addedLinks[0].type)));
    return new Promise((resolve, reject) =>
      dispatch(linkIssueToEntityAction(linkedIssue.id, addedLinks, resolve, reject))
    );
  };

  const removeRelationships = ({entities}) => {
    const {id: issueId} = entities.find(entity => entity.type === 'issue');
    const removedLink = entities.find(entity => entity.type !== 'issue');
    return new Promise((resolve, reject) =>
      dispatch(unlinkIssueFromEntityAction(issueId, removedLink, resolve, reject))
    );
  };

  const onAddReferenceClick = () => {
    trackEvent(openAddReferenceModal(viewType));
  };

  const createFormProps = useMemo(
    () => ({
      initialFields: {
        title: i18n.t('issueAttributes.linkedReferences.rfiTitle', {
          title: issue[ISSUE_ATTRIBUTE_NAMES.TITLE],
          identifier: issue[ISSUE_ATTRIBUTE_NAMES.ID]
        }),
        description: issue[ISSUE_ATTRIBUTE_NAMES.DESCRIPTION]
          ? i18n.t('issueAttributes.linkedReferences.rfiQuestion', {
            question: issue[ISSUE_ATTRIBUTE_NAMES.DESCRIPTION],
            identifier: issue[ISSUE_ATTRIBUTE_NAMES.ID]
          })
          : '',
        dueDate: issue[ISSUE_ATTRIBUTE_NAMES.DUE_DATE],
        assignedTo: issue[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO],
        locationDescription: issue[ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS],
        linkedDocument: getRfiFormattedLinkedDocument(issue),
        attachmentsAttributes: featureFlags.get(FEATURE_FLAGS.ENTITY_LINKER_RFI_ATTACHMENTS)
          ? getRfiFormattedAttachmentsAttributes(attachments)
          : [],
        currentSheet: getRfiFormattedSheetWithViewableId(issue)
      },
      sourceEntityType: ENTITY_TYPES.ISSUE,
      sourceEntityNumber: `#${issue[ISSUE_ATTRIBUTE_NAMES.ID]}`,
      currentProjectEntitlements: currentProject,
      origin: viewType === viewTypes.DOCUMENT ? 'Issues Docs' : 'Issues',
      currentUser,
      usersInProject: updatedUsers,
      rolesInProject,
      companiesInProject
    }),
    [issue, attachments, currentProject, viewType, currentUser, updatedUsers, rolesInProject, companiesInProject]
  );

  return (
    <div className={classes}>
      <EntityLinker
        sourceEntityId={issue.id}
        sourceEntityType={ENTITY_TYPES.ISSUE}
        plugins={[meetingsPlugin, rfisPlugin, assetsPlugin]}
        referenceListTitle={i18n.t('issueAttributes.linkedReferences.label')}
        container={container}
        createFormProps={createFormProps}
        allowAddingLinks={allowEditingLinks}
        onAddRelationships={addRelationships}
        allowRemoveLinkedEntity={allowEditingLinks}
        onRemove={removeRelationships}
        onAddReferenceClick={onAddReferenceClick}
      />
    </div>
  );
};

IssuePreviewDetailsTabLinkedEntitiesSection.displayName = 'IssuePreviewDetailsTabLinkedEntitiesSection';

IssuePreviewDetailsTabLinkedEntitiesSection.propTypes = {
  issue: issueAttributesType.isRequired,
  attachments: PropTypes.array.isRequired,
  currentUser: PropTypes.object.isRequired,
  currentProject: PropTypes.object.isRequired,
  usersInProject: PropTypes.array.isRequired,
  rolesInProject: PropTypes.array.isRequired,
  companiesInProject: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
  viewType: PropTypes.string.isRequired,
  container: PropTypes.object,
  className: PropTypes.string
};

const mapStateToProps = state => ({
  viewType: getCurrentViewType(state),
  currentUser: getCurrentUserInMcFormat(state),
  currentProject: getCurrentProject(state),
  oxygen: getAllOxygenMappings(state),
  usersInProject: getActiveUsers(state),
  rolesInProject: getProjectRoles(state),
  companiesInProject: getProjectCompanies(state),
  attachments: getAttachmentsList(state)
});

export default connect(mapStateToProps)(IssuePreviewDetailsTabLinkedEntitiesSection);
